import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import './PortfolioChart.css';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const PortfolioChart = ({ weights }) => {
  // Filter out very small allocations for cleaner visualization
  const significantWeights = Object.entries(weights)
    .filter(([_, data]) => data.weight > 0.01) // More than 1%
    .sort((a, b) => b[1].weight - a[1].weight);

  // Prepare data for chart
  const chartData = {
    labels: significantWeights.map(([code, data]) => data.name),
    datasets: [{
      data: significantWeights.map(([_, data]) => (data.weight * 100).toFixed(2)),
      backgroundColor: [
        '#FF6384',
        '#36A2EB',
        '#FFCE56',
        '#4BC0C0',
        '#9966FF',
        '#FF9F40',
        '#FF6384',
        '#C9CBCF',
        '#4BC0C0',
        '#FF6384'
      ],
      borderWidth: 2,
      borderColor: '#fff',
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i];
                return {
                  text: `${label} (${value}%)`,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          },
          font: {
            size: 12
          },
          padding: 10
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            return `${label}: ${value}%`;
          }
        }
      },
      title: {
        display: true,
        text: 'Portfolio Allocation',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: 20
      }
    }
  };

  // Download chart as image
  const downloadChart = () => {
    const canvas = document.getElementById('portfolio-pie-chart');
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = 'portfolio-allocation.png';
    link.href = url;
    link.click();
  };

  return (
    <div className="portfolio-chart">
      <div className="chart-container">
        <Pie 
          id="portfolio-pie-chart"
          data={chartData} 
          options={options}
        />
      </div>
      
      <div className="chart-actions">
        <button onClick={downloadChart} className="download-button">
          📥 Download Chart
        </button>
      </div>

      <div className="allocation-summary">
        <h4>Allocation Summary</h4>
        <div className="summary-stats">
          <div className="stat">
            <span className="stat-label">Total Funds:</span>
            <span className="stat-value">{significantWeights.length}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Largest Allocation:</span>
            <span className="stat-value">
              {(significantWeights[0][1].weight * 100).toFixed(1)}% ({significantWeights[0][1].name})
            </span>
          </div>
          <div className="stat">
            <span className="stat-label">Concentration (Top 3):</span>
            <span className="stat-value">
              {(significantWeights.slice(0, 3).reduce((sum, [_, data]) => sum + data.weight, 0) * 100).toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioChart;